import type {
  ApiDashboardAggregate,
  FocalAndComparisonAggregate,
  pinType,
} from 'src/types/dashboard';

import type { StackedBarRow } from './constants';

export const allowedPins: pinType[] = [
  'pin1',
  'pin2',
  'pin3',
  'pin4',
  'pin5',
  'pin6',
];

/*
When dataseries is provided, its dynamic content will be extracted and used to customize the bars (serieslabels,
colors, fontColors) as well as the table (serieslabels, attributes), otherwise when no dataseries is provided,
the default content will be used which only uses 5 pins.
favAttribute is used to determine the attribute to be used in the fav column of the table (ptop2, mean, etc.)
diffAttribute is used to determine the attribute to be used in the diff column of the table (ptop2_yoyDiff, etc.)
ExtraPin is used to add 6th pin to the stack when needed, which is not part of the default pins.
 */
export const getStackedBarRows = (
  focalAndComparisonAggregates: FocalAndComparisonAggregate[],
  skipFirstQuestion: boolean,
  sortedPins: pinType[] | undefined,
  favAttribute?: string,
  diffAttribute?: string,
  extraPin = false,
) => {
  const stackedBarRows: StackedBarRow[] = [];

  focalAndComparisonAggregates.forEach(
    (
      { focalAggregate, comparisonAggregate }: FocalAndComparisonAggregate,
      index: number,
    ) => {
      if (skipFirstQuestion && index === 0) {
        return;
      }

      let stack: (number | undefined | null)[] = [];
      if (focalAggregate && sortedPins) {
        for (const pin of Object.values(sortedPins)) {
          if (
            allowedPins.includes(pin) &&
            Object.keys(focalAggregate).includes(pin)
          ) {
            stack.push(focalAggregate[pin]);
          }
        }
      } else if (focalAggregate) {
        const startIndex = extraPin ? 0 : 1;
        stack = [...allowedPins]
          .reverse()
          .slice(startIndex)
          .map((pin) => focalAggregate?.[pin]);
      }

      const favValue =
        favAttribute && focalAggregate && favAttribute in focalAggregate
          ? (focalAggregate[
              favAttribute as keyof ApiDashboardAggregate
            ] as number)
          : undefined;

      // comparison: ensure this compares to favAttribute or otherwise ptop2 as fallback - but only if available
      let comparisonValue;
      if (favValue === undefined) {
        comparisonValue = comparisonAggregate?.ptop2;
      } else if (
        favAttribute &&
        comparisonAggregate &&
        favAttribute in comparisonAggregate
      ) {
        comparisonValue = comparisonAggregate[
          favAttribute as keyof ApiDashboardAggregate
        ] as number;
      }

      const stackedBarRow = {
        label: focalAggregate?.label,
        stack,
        fav: favValue ? favValue : focalAggregate?.ptop2,
        diff:
          diffAttribute && focalAggregate
            ? (focalAggregate[
                diffAttribute as keyof ApiDashboardAggregate
              ] as number)
            : focalAggregate?.ptop2_yoyDiff,

        comparison: comparisonValue
          ? comparisonValue
          : comparisonAggregate?.ptop2,
      };

      stackedBarRows.push(stackedBarRow);
    },
  );

  return stackedBarRows;
};
